// Libs
import { useNavigate } from "react-router-dom";
import EnvelopeIcon from "@heroicons/react/24/outline/EnvelopeIcon";
import DocumentTextIcon from "@heroicons/react/24/outline/DocumentTextIcon";
import RocketLaunchIcon from "@heroicons/react/24/outline/RocketLaunchIcon";
import PlusIcon from "@heroicons/react/24/solid/PlusIcon";
import ChartBarIcon from "@heroicons/react/24/outline/ChartBarIcon";
import EyeIcon from "@heroicons/react/24/outline/EyeIcon";
import CalendarIcon from "@heroicons/react/24/outline/CalendarIcon";
import CursorArrowRaysIcon from "@heroicons/react/24/outline/CursorArrowRaysIcon";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import { PhotoIcon } from "@heroicons/react/24/outline";

// Local
import HeadingOne from "../../components/HeadingOne";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import {
  ExistingEmailTemplate,
  EmailTemplateStatus,
} from "../../models/email-template";
import {
  ExistingEmailCampaign,
  EmailCampaignStatus,
} from "../../models/email-campaign";
import { EmailCampaignStatistics } from "../../database/email-marketing";
import {
  EMAIL_TEMPLATES_URL,
  EMAIL_TEMPLATES_CREATE_URL,
  EMAIL_CAMPAIGNS_URL,
  EMAIL_CAMPAIGNS_CREATE_URL,
  EMAIL_TEMPLATES_EDIT_URL,
  EMAIL_CAMPAIGNS_DETAILS_URL,
  MARKETING_IMAGES_URL,
} from "../../urls";

interface Props {
  siteKey: string;
  templates: ExistingEmailTemplate[];
  campaigns: ExistingEmailCampaign[];
  campaignStatistics: Map<string, EmailCampaignStatistics>;
  loadingTemplates: boolean;
  loadingCampaigns: boolean;
  loadingStatistics: boolean;
  error: string | null;
}

export default function EmailMarketingPage({
  siteKey,
  templates,
  campaigns,
  campaignStatistics,
  loadingTemplates,
  loadingCampaigns,
  loadingStatistics,
  error,
}: Props) {
  const navigate = useNavigate();

  const handleCreateTemplate = () => {
    navigate(EMAIL_TEMPLATES_CREATE_URL);
  };

  const handleCreateCampaign = () => {
    navigate(EMAIL_CAMPAIGNS_CREATE_URL);
  };

  const handleViewTemplates = () => {
    navigate(EMAIL_TEMPLATES_URL);
  };

  const handleViewCampaigns = () => {
    navigate(EMAIL_CAMPAIGNS_URL);
  };

  const handleEditTemplate = (templateId: string) => {
    navigate(`${EMAIL_TEMPLATES_EDIT_URL}/${templateId}`);
  };

  const handleViewCampaign = (campaignId: string) => {
    navigate(EMAIL_CAMPAIGNS_DETAILS_URL.replace(":campaignId", campaignId));
  };

  const handleViewMarketingImages = () => {
    navigate(MARKETING_IMAGES_URL);
  };

  // Get statistics
  const templateStats = {
    total: templates.length,
    active: templates.filter((t) => t.status === EmailTemplateStatus.ACTIVE)
      .length,
    draft: templates.filter((t) => t.status === EmailTemplateStatus.DRAFT)
      .length,
    archived: templates.filter((t) => t.status === EmailTemplateStatus.ARCHIVED)
      .length,
  };

  const campaignStats = {
    total: campaigns.length,
    completed: campaigns.filter(
      (c) => c.status === EmailCampaignStatus.COMPLETED,
    ).length,
    sending: campaigns.filter((c) => c.status === EmailCampaignStatus.SENDING)
      .length,
    pending: campaigns.filter((c) => c.status === EmailCampaignStatus.PENDING)
      .length,
    failed: campaigns.filter((c) => c.status === EmailCampaignStatus.FAILED)
      .length,
  };

  // Get recent items (last 5)
  const recentTemplates = templates
    .sort(
      (a, b) =>
        b.timestampLastModified.toMillis() - a.timestampLastModified.toMillis(),
    )
    .slice(0, 5);

  const recentCampaigns = campaigns
    .sort(
      (a, b) => b.timestampCreated.toMillis() - a.timestampCreated.toMillis(),
    )
    .slice(0, 5);

  // Calculate 30-day metrics using the statistics from the container
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  // Filter campaigns from past 30 days
  const recentCampaigns30Days = campaigns.filter((campaign) => {
    if (
      !campaign.timestampCreated ||
      campaign.status !== EmailCampaignStatus.COMPLETED
    ) {
      return false;
    }
    const campaignDate = campaign.timestampCreated.toDate();
    return campaignDate >= thirtyDaysAgo;
  });

  // Calculate totals using the statistics data
  const totals30Days = recentCampaigns30Days.reduce(
    (acc, campaign) => {
      const stats = campaignStatistics.get(campaign.id);

      if (stats) {
        // Ensure all values are numbers and not undefined/null
        const sent = Number(stats.sent) || 0;
        const opens = Number(stats.opens) || 0;
        const clicked = Number(stats.clicked) || 0;

        acc.sent += sent;
        acc.opens += opens;
        acc.clicks += clicked;
      } else {
        // Fallback to campaign sentCount if statistics not available
        const sentCount = Number(campaign.sentCount) || 0;
        acc.sent += sentCount;
      }
      return acc;
    },
    { sent: 0, opens: 0, clicks: 0 },
  );

  const openRate30Days =
    totals30Days.sent > 0 ? (totals30Days.opens / totals30Days.sent) * 100 : 0;

  const clickRate30Days =
    totals30Days.sent > 0 ? (totals30Days.clicks / totals30Days.sent) * 100 : 0;

  const getStatusIcon = (status: EmailCampaignStatus) => {
    switch (status) {
      case EmailCampaignStatus.COMPLETED:
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case EmailCampaignStatus.SENDING:
        return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case EmailCampaignStatus.PENDING:
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case EmailCampaignStatus.FAILED:
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case EmailCampaignStatus.CANCELLED:
        return <XCircleIcon className="h-4 w-4 text-gray-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: EmailCampaignStatus) => {
    switch (status) {
      case EmailCampaignStatus.COMPLETED:
        return "text-green-700 bg-green-50";
      case EmailCampaignStatus.SENDING:
        return "text-blue-700 bg-blue-50";
      case EmailCampaignStatus.PENDING:
        return "text-yellow-700 bg-yellow-50";
      case EmailCampaignStatus.FAILED:
        return "text-red-700 bg-red-50";
      case EmailCampaignStatus.CANCELLED:
        return "text-gray-700 bg-gray-50";
      default:
        return "text-gray-700 bg-gray-50";
    }
  };

  const getTemplateStatusColor = (status: EmailTemplateStatus) => {
    switch (status) {
      case EmailTemplateStatus.ACTIVE:
        return "bg-green-100 text-green-800";
      case EmailTemplateStatus.DRAFT:
        return "bg-yellow-100 text-yellow-800";
      case EmailTemplateStatus.ARCHIVED:
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    return timestamp.toDate().toLocaleDateString();
  };

  const isLoading = loadingTemplates || loadingCampaigns;

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Loading Data
              </h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <HeadingOne>Email Marketing Dashboard</HeadingOne>
      </div>

      {/* Welcome Section - Only show if no templates or campaigns */}
      {!isLoading && templates.length === 0 && campaigns.length === 0 && (
        <div className="mb-8 rounded-lg bg-blue-50 p-6">
          <h3 className="mb-2 text-lg font-medium text-blue-900">
            Welcome to Email Marketing
          </h3>
          <div className="space-y-2 text-blue-800">
            <p>
              1. <strong>Create your first email template</strong> with
              personalized content and variables
            </p>
            <p>
              2. <strong>Design your campaign</strong> by selecting customers
              and template
            </p>
            <p>
              3. <strong>Send and track</strong> your email campaign performance
            </p>
            <p>
              4. <strong>Analyze results</strong> to improve future campaigns
            </p>
          </div>
          <div className="mt-4">
            <BaseButtonPrimary onClick={handleCreateTemplate}>
              Start with a Template
            </BaseButtonPrimary>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Emails Sent (past 30 days) */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EnvelopeIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Emails Sent (past 30 days)
              </h3>
              <div className="mt-1">
                <span className="text-2xl font-bold text-purple-600">
                  {isLoading ? "..." : totals30Days.sent.toLocaleString()}
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {loadingStatistics
                  ? "Loading campaign data..."
                  : `From ${recentCampaigns30Days.length} completed campaigns`}
              </div>
            </div>
          </div>
        </div>

        {/* Open Rate (past 30 days) */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EyeIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Open Rate (past 30 days)
              </h3>
              <div className="mt-1">
                <span className="text-2xl font-bold text-orange-600">
                  {isLoading
                    ? "..."
                    : loadingStatistics
                      ? "..."
                      : totals30Days.sent > 0
                        ? `${openRate30Days.toFixed(1)}%`
                        : "N/A"}
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {loadingStatistics
                  ? "Loading engagement data..."
                  : `${totals30Days.opens.toLocaleString()} opens from ${totals30Days.sent.toLocaleString()} emails`}
              </div>
            </div>
          </div>
        </div>

        {/* Click Rate (past 30 days) */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CursorArrowRaysIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Click Rate (past 30 days)
              </h3>
              <div className="mt-1">
                <span className="text-2xl font-bold text-indigo-600">
                  {isLoading
                    ? "..."
                    : loadingStatistics
                      ? "..."
                      : totals30Days.sent > 0
                        ? `${clickRate30Days.toFixed(1)}%`
                        : "N/A"}
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-500">
                {loadingStatistics
                  ? "Loading engagement data..."
                  : `${totals30Days.clicks.toLocaleString()} clicks from ${totals30Days.sent.toLocaleString()} emails`}
              </div>
            </div>
          </div>
        </div>

        {/* Marketing Images */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <PhotoIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">
                Marketing Images
              </h3>
              <div className="mt-1">
                <span className="text-2xl font-bold text-green-600">
                  Manage
                </span>
              </div>
              <div className="mt-2">
                <BaseButtonSecondary
                  onClick={handleViewMarketingImages}
                  className="text-sm"
                >
                  View Gallery
                </BaseButtonSecondary>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Items */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Recent Templates */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Recent Templates
            </h2>
            <div className="flex gap-2">
              <BaseButtonPrimary
                onClick={handleCreateTemplate}
                className="text-sm"
              >
                Create Template
              </BaseButtonPrimary>
              <BaseButtonSecondary
                onClick={handleViewTemplates}
                className="text-sm"
              >
                View All
              </BaseButtonSecondary>
            </div>
          </div>

          {isLoading ? (
            <div className="py-8 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="mt-2 text-gray-500">Loading templates...</p>
            </div>
          ) : recentTemplates.length === 0 ? (
            <div className="py-8 text-center">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No templates yet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Create your first email template to get started.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTemplates.map((template) => (
                <div
                  key={template.id}
                  className="cursor-pointer rounded-md border border-gray-200 p-3 hover:bg-gray-50"
                  onClick={() => handleEditTemplate(template.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {template.name}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {template.subject}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getTemplateStatusColor(template.status)}`}
                      >
                        {template.status}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center text-xs text-gray-400">
                    <CalendarIcon className="mr-1 h-3 w-3" />
                    Modified {formatDate(template.timestampLastModified)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Campaigns */}
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">
              Recent Campaigns
            </h2>
            <div className="flex gap-2">
              <BaseButtonPrimary
                onClick={handleCreateCampaign}
                className="text-sm"
              >
                Create Campaign
              </BaseButtonPrimary>
              <BaseButtonSecondary
                onClick={handleViewCampaigns}
                className="text-sm"
              >
                View All
              </BaseButtonSecondary>
            </div>
          </div>

          {isLoading ? (
            <div className="py-8 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="mt-2 text-gray-500">Loading campaigns...</p>
            </div>
          ) : recentCampaigns.length === 0 ? (
            <div className="py-8 text-center">
              <RocketLaunchIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No campaigns yet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Create your first email campaign to get started.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentCampaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="cursor-pointer rounded-md border border-gray-200 p-3 hover:bg-gray-50"
                  onClick={() => handleViewCampaign(campaign.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {campaign.campaignName}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {campaign.templateName} • {campaign.totalRecipients}{" "}
                        recipients
                      </p>
                    </div>
                    <div className="ml-2 flex flex-shrink-0 items-center gap-2">
                      {getStatusIcon(campaign.status)}
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(campaign.status)}`}
                      >
                        {campaign.status}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center text-xs text-gray-400">
                    <CalendarIcon className="mr-1 h-3 w-3" />
                    Created {formatDate(campaign.timestampCreated)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
